import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import { Activity, Loader2, TrendingUp } from "lucide-react";

const pct = (n) => `${Math.round((n || 0) * 100)}%`;

// Reply-rate leaderboard per nudge template. Pulls from
// /api/platform/nudge/analytics — reply-rate is computed by matching inbound
// SMS from the tenant's admin phone within 14 days after each nudge send.
export default function TemplateAnalytics({ days = 60 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/platform/nudge/analytics?days=${days}`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) {
    return (
      <Card className="p-5 mb-6" data-testid="template-analytics-loading">
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Computing template reply rates…
        </div>
      </Card>
    );
  }
  if (!data) return null;
  const rows = data.rows || [];
  const totals = data.totals || {};

  return (
    <Card className="p-5 mb-6" data-testid="template-analytics">
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp className="w-4 h-4 text-[hsl(var(--gold))]" />
        <div className="font-mono-label text-muted-foreground text-[0.65rem] tracking-widest">TEMPLATE ANALYTICS · LAST {data.days}D</div>
      </div>
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
        <h2 className="font-display text-2xl font-bold text-navy">Which lines actually get replies?</h2>
        <div className="flex items-center gap-3 text-[0.7rem] text-muted-foreground">
          <span>{totals.sent || 0} sent · {totals.replied || 0} replied · <strong className="text-navy">{pct(totals.overall_reply_rate)}</strong> overall</span>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">No nudges sent in the last {data.days} days — fire a template from /platform and reply data will populate.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="template-analytics-table">
            <thead>
              <tr className="text-[0.6rem] font-mono-label tracking-widest text-muted-foreground">
                <th className="text-left px-2 py-2">Template</th>
                <th className="text-right px-2 py-2">Sent</th>
                <th className="text-right px-2 py-2">Live</th>
                <th className="text-right px-2 py-2">Mock</th>
                <th className="text-right px-2 py-2">Replied</th>
                <th className="text-right px-2 py-2 w-40">Reply rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {rows.map((r) => {
                const rate = r.reply_rate;
                const barColor = rate >= 0.25 ? "bg-emerald-500" : rate >= 0.10 ? "bg-amber-500" : "bg-muted-foreground/40";
                return (
                  <tr key={r.template} data-testid={`analytics-row-${r.template}`} className="hover:bg-muted/20">
                    <td className="px-2 py-3">
                      <div className="font-semibold text-navy">{r.label}</div>
                      <div className="text-[0.6rem] font-mono text-muted-foreground/70">{r.template}</div>
                    </td>
                    <td className="px-2 py-3 text-right tabular-nums font-semibold">{r.sent}</td>
                    <td className="px-2 py-3 text-right tabular-nums text-emerald-700">{r.sent_live}</td>
                    <td className="px-2 py-3 text-right tabular-nums text-muted-foreground">{r.mock}</td>
                    <td className="px-2 py-3 text-right tabular-nums text-navy font-semibold">{r.replied}</td>
                    <td className="px-2 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                          <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.min(100, rate * 100)}%` }} />
                        </div>
                        <div className="tabular-nums text-xs font-semibold w-10 text-right">{pct(rate)}</div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="text-[0.7rem] text-muted-foreground mt-3">
        Reply-rate = % of nudges that got any inbound SMS from the tenant within 14 days after send. Templates ranked high→low.
      </div>
    </Card>
  );
}
